import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "../lib/api";

export default function LookBuilderResult() {
  const loc = useLocation();
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const triedRef = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); setError("Missing session id"); return; }

    let cancelled = false;
    const poll = async () => {
      triedRef.current += 1;
      try {
        const { data: r } = await api.get(`/look-builder/result/${sessionId}`);
        if (cancelled) return;
        if (r.status === "ready") { setData(r); setStatus("ready"); return; }
        if (triedRef.current >= 10) { setStatus("timeout"); return; }
        setTimeout(poll, 2500);
      } catch (e) {
        if (cancelled) return;
        if (triedRef.current >= 10) {
          setStatus("error");
          setError(e?.response?.data?.detail || "Couldn't generate your look.");
          return;
        }
        setTimeout(poll, 2500);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [loc.search]);

  if (status === "loading") {
    return (
      <div className="max-w-4xl mx-auto px-4 py-24 text-center" data-testid="lbr-loading">
        <div className="overline mb-3">Generating</div>
        <h1 className="font-serif-display text-4xl text-stone-900">Building your look…</h1>
        <p className="mt-3 text-stone-600">Confirming payment and curating brushes. This takes about 15 seconds.</p>
      </div>
    );
  }

  if (status === "error" || status === "timeout") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="lbr-error">
        <div className="overline mb-3">Hmm</div>
        <h1 className="font-serif-display text-4xl text-stone-900">We hit a snag.</h1>
        <p className="mt-3 text-stone-600">{error || "Your look is taking longer than expected. If you were charged, refresh this page in a minute or contact support."}</p>
        <Link to="/look-builder" className="inline-block mt-8 text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] text-white px-6 py-3">← Back</Link>
      </div>
    );
  }

  const look = data?.look || {};
  return (
    <div data-testid="lbr-ready" className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
      <div className="overline mb-4">Your Look · {data?.occasion}</div>
      <h1 className="font-serif-display text-5xl sm:text-6xl font-light text-stone-900 tracking-tight leading-[1.05]">
        {look.title}
      </h1>
      {look.summary && <p className="mt-5 text-lg italic text-stone-600 font-light max-w-3xl">{look.summary}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-12">
        <div className="lg:col-span-7">
          <div className="overline mb-4">Step-by-step</div>
          <ol className="space-y-5">
            {(look.steps || []).map((s, i) => (
              <li key={i} className="flex gap-4" data-testid={`lbr-step-${i}`}>
                <span className="font-serif-display text-3xl text-[#8C6A5D] leading-none w-8 shrink-0">{(i + 1).toString().padStart(2, "0")}</span>
                <p className="text-base text-stone-700 leading-relaxed">{s}</p>
              </li>
            ))}
          </ol>
          {look.finishing_tip && (
            <div className="mt-8 bg-[#F2EFEB] p-5 border-l-2 border-[#8C6A5D]">
              <div className="overline mb-1">Finishing tip</div>
              <p className="text-sm text-stone-700 italic">{look.finishing_tip}</p>
            </div>
          )}
        </div>

        <aside className="lg:col-span-5 space-y-8">
          {look.products?.length > 0 && (
            <div>
              <div className="overline mb-3">Products you'll need</div>
              <ul className="space-y-3">
                {look.products.map((p, i) => (
                  <li key={i} className="border-t border-[#E5E0D8] pt-3" data-testid={`lbr-product-${i}`}>
                    <div className="text-sm font-medium text-stone-900">{p.name}</div>
                    {p.purpose && <div className="text-xs text-stone-500 italic mt-1">{p.purpose}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {look.brushes?.length > 0 && (
            <div>
              <div className="overline mb-3">Brushes you'll need</div>
              <ul className="space-y-3">
                {look.brushes.map((b, i) => (
                  <li key={i} className="flex gap-3 items-start" data-testid={`lbr-brush-${i}`}>
                    {b.image && (
                      <Link to={`/brush/${b.id}`} className="w-14 h-16 bg-[#F2EFEB] overflow-hidden shrink-0">
                        <img src={b.image} alt={b.name} className="w-full h-full object-cover" />
                      </Link>
                    )}
                    <div>
                      <Link to={`/brush/${b.id}`} className="font-serif-display text-lg text-stone-900 hover:text-[#8C6A5D]">{b.name}</Link>
                      {b.why && <p className="text-xs text-stone-500 italic mt-1">{b.why}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      <div className="mt-14 flex flex-wrap gap-4">
        <Link to="/look-builder" className="text-xs uppercase tracking-[0.22em] bg-[#8C6A5D] hover:bg-[#70544A] text-white px-6 py-3 transition-colors">Build Another Look</Link>
        <Link to="/shop" className="text-xs uppercase tracking-[0.22em] border border-[#8C6A5D] text-[#8C6A5D] hover:bg-[#F2EFEB] px-6 py-3 transition-colors">Shop the Brushes</Link>
      </div>
      <p className="mt-6 text-xs text-stone-500">
        Bookmark this page — you can revisit your look anytime from this private link.
      </p>
    </div>
  );
}
